<template>
    <b-container class="p-5">
        <b-row class="my-5">
            <b-col class="my-2">
                <date-range-picker
                    class="mr-2" 
                    type="month" 
                    :startDate="dateRange[0]" 
                    :endDate="dateRange[1]" 
                    :customRanges="ranges"
                    @date-select="filterPayablesAnalyticsByDate"></date-range-picker>
            </b-col>
            <b-col class="my-2">
                <multiselect-dropdown
                    v-if="merchantsList.length"
                    :options="merchantsList"
                    :hasMore="hasMoreMerchants"
                    :loadingMore="merchantsLoadingMore"
                    :searchResults="merchantsSearchResults"
                    :searchResultsLoading="merchantsSearchLoading"
                    allLabel="All Merchants"
                    :multiple="true"
                    :sideSingle="true"
                    :value="queryMerchant"
                    @item-select="filterMerchants"
                    @load-more="loadMoreMerchants"
                    @search-input="onMerchantsSearchInput" />
            </b-col>
        </b-row>
        <b-table :items="statsData" striped bordered head-variant="light" hover no-border-collapse show-empty
            :busy="isloading" no-footer-sorting responsive foot-variant="light">
            <template #table-busy>
                <div class="text-center text-primary  my-10">
                    <b-spinner class="align-middle" />
                    <strong class="ml-2">Loading...</strong>
                </div>
            </template>
            <template v-slot:cell()="itemData">
                <span v-if="itemData.field.key == 'items'">{{ itemData.value }}</span>
                <span v-else class="right" v-b-tooltip.hover :title="formatHoverCurrency(itemData.value)">{{
                    itemData.value ? formatThousands(itemData.value) : 0 }}</span>
            </template>
            <template #head()="dataItem">
                <span :class="{ 'right': dataItem.label != 'Items' }">{{ dataItem.label.toUpperCase() }}</span>
            </template>
        </b-table>
    </b-container>
</template>
<script>
import AnalyticsService from "@/services/analytics.service";
import DateRangePicker from "@/views/pages/companies/components/DateRangePicker.vue";
import { DateRanges } from '@/dateRanges.js'
import { DateTime } from "luxon";
import MultiselectDropdown from "../../../components/multiselect.dropdown.vue";
import { GET_ALL_MERCHANTS, LOAD_MORE_MERCHANTS, SEARCH_MERCHANTS, CLEAR_MERCHANTS_SEARCH } from "@/store/monthlydata";
import { mapState } from 'vuex';
import { validateAdminQueryParameter } from "@/store/invoice/helpers";
import { QUERY_PARAMETERS_ACTIONS } from "@/store/queryparams";

export default {
    name: "platform-stats",
    components: {
        DateRangePicker,
        MultiselectDropdown,
    },
    data() {
        return {
            merchant: null,
            isloading: false,
            queryMerchant:null,
            dateRange: [],
            ranges:DateRanges,
            statsData: [],
        }
    },
    created() {
        this.$store.dispatch(QUERY_PARAMETERS_ACTIONS.RESET_QUERY_PARAMS);
        this.$store.dispatch(GET_ALL_MERCHANTS, { is_merchant: "True" })
            .catch(() => {
                this.error = "There was an error loading companies.";
            });
        if (this.$route.query.due_start && this.$route.query.due_end) {
            this.dateRange = [DateTime.fromJSDate(new Date(this.$route.query.due_start)), DateTime.fromJSDate(new Date(this.$route.query.due_end))]
        }
        else {
            this.dateRange = DateRanges['This Year'];
        }
        if( this.$route.query.id){
            this.queryMerchant = this.$route.query.id.split(",");
            this.merchant = this.$route.query.id.split(",");
        }
    },
    computed: {
        ...mapState({
            merchantsList: (state) => state.monthlydata.merchants,
        }),
        hasMoreMerchants() {
            return !!this.$store.state.monthlydata.merchantsNextPageUrl;
        },
        merchantsLoadingMore() {
            return this.$store.state.monthlydata.merchantsLoadingMore;
        },
        merchantsSearchResults() {
            return this.$store.state.monthlydata.merchantsSearchResults || [];
        },
        merchantsSearchLoading() {
            return this.$store.state.monthlydata.merchantsSearchLoading;
        },
    },
    methods: {
        onMerchantsSearchInput(term) {
            if (!term || !term.trim()) {
                this.$store.dispatch(CLEAR_MERCHANTS_SEARCH);
            } else {
                this.$store.dispatch(SEARCH_MERCHANTS, { search: term.trim(), is_merchant: 'True' });
            }
        },
        loadMoreMerchants() {
            this.$store.dispatch(LOAD_MORE_MERCHANTS);
        },
        filterMerchants(event) {
            this.merchant = event?.map((item) => item.id) ?? null;
            this.getAnalytics()
        },
        getDifferencePrevious(selection, refdate) {
            switch (selection) {
                case 'This Month':
                    return refdate.minus({ months: 1 }).toUTC().toString()
                case 'This Quarter':
                    return refdate.minus({ months: 3 }).toUTC().toString()
                case 'This Year':
                    return refdate.minus({ year: 1 }).toUTC().toString()
                default:
                    return refdate.toUTC().toString()
            }
        },
        getAnalytics() {
            this.isloading = true;
            this.$store.dispatch(QUERY_PARAMETERS_ACTIONS.RESET_QUERY_PARAMS);
            this.statsData = [];
            const currentPath = this.$route.path;
            let params = {}
            if (this.dateRange !== void 0) {
                params = {
                    "due_start": this.dateRange[0].toUTC().toString(),
                    "due_end": this.dateRange[1].toUTC().toString(),
                }
                const payload = validateAdminQueryParameter({'due_start':this.dateRange[0].toUTC().toString(),"due_end": this.dateRange[1].toUTC().toString()});
                this.$store.dispatch(QUERY_PARAMETERS_ACTIONS.ADD_FILTER_QUERY_PARAMETER, {payload, currentPath})
            }
            if (this.merchant) {
                params = {
                    ...params,
                    "id": this.merchant.join(',')
                }
                const payload = validateAdminQueryParameter({'id':this.merchant.join(',')});
                this.$store.dispatch(QUERY_PARAMETERS_ACTIONS.ADD_FILTER_QUERY_PARAMETER, {payload, currentPath})
            }
            AnalyticsService.get('platform-stats', params)
                .then(({ data }) => {
                    this.statsData = data;
                    this.isloading = false;
                })
                .catch((error) => {
                    console.log(error);
                    this.$showAlert(error.message, 'danger');
                })
                .finally(() => {
                    this.isloading = false;
                });
        },
        filterPayablesAnalyticsByDate(date_range) {
            let { startDate, endDate, label } = date_range;
            if (typeof startDate === 'string') {
                startDate = DateTime.fromISO(startDate);
            }
            if (typeof endDate === 'string') {
                endDate = DateTime.fromISO(endDate);
            }
            this.dateRange = [startDate, endDate, label];
            this.getAnalytics(this.merchant)
        },
        formatThousands(amount) {
            const formattedAmount = new Intl.NumberFormat("en-US", {
                maximumFractionDigits: 2,
                minimumFractionDigits: 2,
            }).format(amount);

            let splitAmount = formattedAmount.split(".");
            return splitAmount[0]
        },
        formatHoverCurrency(amount) {
            const formattedAmount = new Intl.NumberFormat("en-US", {
                maximumFractionDigits: 2,
                minimumFractionDigits: 2,
                roundingIncrement: 1,
            }).format(amount);

            return formattedAmount
        },
    },
}
</script>

<style lang="scss">
@import './index.scss';
</style>